import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import DynamicForm from '../components/DynamicForm';
import InlinePaymentSection, { availablePaymentMethods } from '../components/InlinePaymentSection';
import { useToast } from '../components/Toast';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime } from '../utils/datetime';
import { editableSchema, valueForLabel } from '../utils/formInitialValues';
import { AlertCircle, Calendar, Clock, Download, MapPin, Pencil, XCircle } from 'lucide-react';

const STATUS_STYLES = {
  REGISTERED: 'bg-green-100 text-green-800',
  PAID: 'bg-green-100 text-green-800',
  PENDING_FOR_APPROVAL: 'bg-yellow-100 text-yellow-800',
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800',
  CANCELLED: 'bg-gray-200 text-gray-700',
  REJECTED: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  REGISTERED: 'Confirmed',
  PAID: 'Confirmed',
  PENDING_FOR_APPROVAL: 'Awaiting approval',
  PENDING_PAYMENT: 'Payment pending',
  PAYMENT_FAILED: 'Payment failed',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
};

const flatFields = (fields = []) => fields.flatMap((field) => (field ? [
  field,
  ...(field.optionsWithPricing || []).flatMap((option) => flatFields(option?.subFields || [])),
] : []));

const shown = (value) => (Array.isArray(value) ? value.join(', ') : String(value ?? ''));

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ChangeList = ({ changes = [] }) => (
  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white text-sm">
    {changes.map((change) => (
      <li key={change.label} className="px-3 py-2">
        <div className="font-medium text-gray-800">{change.label}</div>
        <div className="text-gray-600 break-words">
          <span className="line-through">{change.from || '—'}</span>
          <span className="mx-2">→</span>
          <span className="text-gray-900">{change.to || '—'}</span>
        </div>
      </li>
    ))}
  </ul>
);

const MyRegistrationPage = () => {
  const { token } = useParams();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [mode, setMode] = useState('view');
  const [pendingForm, setPendingForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const paymentDraftRef = useRef({});

  const { data: view, isLoading, error } = useQuery({
    queryKey: ['myRegistration', token],
    queryFn: () => ApiService.getMyRegistration(token),
    retry: false,
  });

  const { data: event } = useQuery({
    queryKey: ['publicEvent', view?.eventId],
    queryFn: () => ApiService.getPublicEvent(view.eventId),
    enabled: !!view?.eventId,
    retry: false,
  });

  const amountDue = preview?.amountDue || 0;
  const { data: paymentSettingsResponse } = useQuery({
    queryKey: ['publicEventPaymentSettings', view?.eventId],
    queryFn: () => ApiService.getPublicEventPaymentSettings(view.eventId),
    enabled: !!view?.eventId && amountDue > 0,
    retry: false,
  });
  const paymentSettings = (Array.isArray(paymentSettingsResponse) ? paymentSettingsResponse[0] : paymentSettingsResponse)
    || event?.paymentSettings || null;

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading your registration…</div>;
  }
  if (error || !view) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-2">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto" />
            <p className="font-medium">{error?.message || 'This link is not valid.'}</p>
            <p className="text-sm text-gray-500">Use the link from your registration email, or ask the organiser for help.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currency = view.currency || event?.currency || 'INR';
  const schema = (view.onSpot && event?.onSpotFormSchema?.length ? event.onSpotFormSchema : event?.formSchema) || [];
  const answers = flatFields(schema)
    .map((field) => ({ label: field.label, value: valueForLabel(view.formData, field.label) }))
    .filter((row, index, rows) => row.value != null && shown(row.value) !== ''
      && rows.findIndex((r) => r.label === row.label) === index);

  const refresh = (next) => {
    queryClient.setQueryData(['myRegistration', token], next);
    setMode('view');
    setPreview(null);
    setPendingForm(null);
  };

  const handleReview = async (formData) => {
    setBusy(true);
    try {
      const result = await ApiService.previewMyRegistrationEdit(token, formData);
      if (!result.changes?.length) {
        showError('You haven\'t changed anything yet.');
        return;
      }
      paymentDraftRef.current = {};
      setPendingForm(formData);
      setPreview(result);
      setMode('review');
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    let paymentData;
    if (amountDue > 0) {
      const draft = paymentDraftRef.current || {};
      if (availablePaymentMethods(paymentSettings).length && !draft.paymentMethod) {
        showError('Please select how you paid the extra amount.');
        return;
      }
      if (!draft.transactionId && !draft.documentId) {
        showError('Add the transaction ID or upload the payment screenshot.');
        return;
      }
      paymentData = draft;
    }
    setBusy(true);
    try {
      const next = await ApiService.editMyRegistration(token, pendingForm, paymentData);
      showSuccess(amountDue > 0
        ? 'Sent to the organiser. Your change applies once they confirm the payment.'
        : 'Your registration is updated.');
      refresh(next);
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      const next = await ApiService.cancelMyRegistration(token, cancelReason);
      setCancelOpen(false);
      showSuccess('Your registration is cancelled.');
      refresh(next);
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadReceipt = async () => {
    try {
      const blob = await ApiService.downloadMyReceipt(token);
      saveBlob(blob, blob.type === 'application/zip' ? `${view.userId}_payment_documents.zip` : `${view.userId}_receipt.pdf`);
    } catch (e) {
      showError(e.message);
    }
  };

  const logo = event?.formLogoUrl || event?.eventLogo;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-6 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              {logo && event?.showFormLogo !== false && (
                <img src={logo} alt="" className="h-12 w-12 object-contain rounded" />
              )}
              <div className="min-w-0 flex-1">
                <CardTitle className="text-xl">{view.eventName}</CardTitle>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                  {event?.eventDate && (
                    <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{formatDate(event.eventDate)}</span>
                  )}
                  {event?.location && (
                    <span className="flex items-center"><MapPin className="h-4 w-4 mr-1" />{event.location}</span>
                  )}
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[view.status] || 'bg-gray-100 text-gray-700'}`}>
                {STATUS_LABELS[view.status] || view.status}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {view.qrCodeBase64 && (
              <div className="flex flex-col items-center gap-2">
                <img src={`data:image/png;base64,${view.qrCodeBase64}`} alt="Your entry QR code" className="h-48 w-48" />
                <div className="text-sm text-gray-600">Registration ID: <span className="font-mono">{view.userId}</span></div>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={`data:image/png;base64,${view.qrCodeBase64}`} download={`${view.userId}_qr.png`}>
                      <Download className="h-4 w-4 mr-1" /> QR code
                    </a>
                  </Button>
                  {view.receiptAvailable && (
                    <Button variant="outline" size="sm" onClick={downloadReceipt}>
                      <Download className="h-4 w-4 mr-1" /> Receipt
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Registered on</div>
                <div>{formatDateTime(view.registeredAt)}</div>
              </div>
              {view.amountPaid > 0 && (
                <div>
                  <div className="text-gray-500">Amount</div>
                  <div>{formatCurrency(view.amountPaid, currency)}</div>
                </div>
              )}
            </div>
            {view.pendingChange && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <div className="flex items-center text-sm font-medium text-amber-800">
                  <Clock className="h-4 w-4 mr-1" />
                  Change awaiting the organiser ({formatCurrency(view.pendingChange.amountDue, currency)} extra)
                </div>
                <ChangeList changes={view.pendingChange.changes} />
                <p className="text-xs text-amber-700">Until it's approved, the details below stay as they were.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {mode === 'view' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-lg">Your details</CardTitle>
                {view.canEdit && (
                  <Button size="sm" onClick={() => setMode('edit')}>
                    <Pencil className="h-4 w-4 mr-1" /> {view.pendingChange ? 'Change request' : 'Edit'}
                  </Button>
                )}
              </div>
              {view.canEdit && view.editUntil && (
                <p className="text-xs text-gray-500">Changes are open until {formatDateTime(view.editUntil)} IST.</p>
              )}
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-gray-100 text-sm">
                {answers.map((row) => (
                  <div key={row.label} className="py-2 grid grid-cols-3 gap-2">
                    <dt className="text-gray-500">{row.label}</dt>
                    <dd className="col-span-2 break-words">{shown(row.value)}</dd>
                  </div>
                ))}
              </dl>
              {view.canCancel && (
                <div className="mt-4 border-t pt-4">
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelOpen(true)}>
                    <XCircle className="h-4 w-4 mr-1" /> Cancel registration
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {mode === 'edit' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Edit your details</CardTitle>
              <p className="text-sm text-gray-500">Your email and uploaded files can't be changed here.</p>
            </CardHeader>
            <DynamicForm
              formSchema={editableSchema(schema)}
              initialValues={pendingForm || view.formData}
              onSubmit={handleReview}
              submitLabel="Review changes"
              isLoading={busy}
              eventId={view.eventId}
              eventData={event}
            />
            <div className="px-6 pb-4">
              <Button variant="ghost" onClick={() => setMode('view')} disabled={busy}>Back</Button>
            </div>
          </Card>
        )}

        {mode === 'review' && preview && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Review your changes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ChangeList changes={preview.changes} />
              {amountDue > 0 ? (
                <>
                  <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                    These changes cost <b>{formatCurrency(amountDue, currency)}</b> more. Pay the difference and add the
                    payment details below. The organiser will confirm it before your registration is updated.
                  </div>
                  <InlinePaymentSection
                    amount={amountDue}
                    paymentSettings={paymentSettings}
                    eventId={view.eventId}
                    onChange={(draft) => { paymentDraftRef.current = draft; }}
                  />
                </>
              ) : (
                <p className="text-sm text-gray-600">No extra payment is needed for these changes.</p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleConfirm} disabled={busy}>
                  {amountDue > 0 ? 'Send for approval' : 'Save changes'}
                </Button>
                <Button variant="outline" onClick={() => setMode('edit')} disabled={busy}>Back to edit</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <ConfirmationDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancel}
        title="Cancel your registration?"
        message="Your QR code will stop working. This can't be undone online."
        confirmText="Cancel registration"
        cancelText="Keep it"
        isLoading={busy}
        inputLabel="Reason (optional)"
        inputValue={cancelReason}
        onInputChange={setCancelReason}
      />
    </div>
  );
};

export default MyRegistrationPage;
